
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import moment from 'moment';
import { GobiColor } from '@/helpers/color';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';

@Component({
	components: {
		StackedBarChartFilterable,
		DateFilter,
	},
})
export default class SupplierPaymentAnalysisBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'paymentAnalysisYear'
	> = ['suppliers', 'agents', 'paymentAnalysisYear'];

	public xAxisData: string[] = [];
	public series: echarts.ECharts[] = [];

	public selectedStartDateOfYear: number = moment().startOf('year').valueOf();

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get tooltipFormatter() {
		return TooltipFormatter.basicWithStackShare;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.basicWithRowShare;
	}

	public get expensiveHook() {
		const { selectedStartDateOfYear, selectedAgents, selectedSuppliers } = this;
		return JSON.stringify([
			selectedStartDateOfYear,
			selectedAgents,
			this.selectedSuppliers,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);
		const msInDay = 24 * 60 * 60 * 1000;
		const year = moment(this.selectedStartDateOfYear).year();

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const table = getTableName('supplierKos');

		let supplierFilter = '';
		if (this.selectedSuppliers.length > 0) {
			const list = this.selectedSuppliers.map((c) => `'${escapeStr(c)}'`).join(',');
			supplierFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		// Compute month boundaries for the selected year
		const monthStarts: number[] = [];
		for (let i = 0; i <= 12; i++) {
			monthStarts.push(moment().year(year).month(i).startOf('month').valueOf());
		}
		const monthCases = Array.from({ length: 12 }, (unused, i) =>
			`WHEN dd >= ${monthStarts[i]} AND dd < ${monthStarts[i + 1]} THEN ${i}`,
		).join(' ');

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['supplierKos']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		const result = await queryHead.rawQuery(`
			SELECT
				CASE ${monthCases} END as mi,
				SUM(CASE WHEN dl <= 0 THEN ag ELSE 0 END) as on_time,
				SUM(CASE WHEN dl > 0 AND dl <= ${30 * msInDay} THEN ag ELSE 0 END) as thirty_days,
				SUM(CASE WHEN dl > ${30 * msInDay} THEN ag ELSE 0 END) as over_thirty
			FROM (
				SELECT
					TRY_CAST("TODOCDUEDATE" AS BIGINT) as dd,
					(TRY_CAST("FROMDOCDATE" AS BIGINT) - TRY_CAST("TODOCDUEDATE" AS BIGINT)) as dl,
					(TRY_CAST("KOLOCALAMT" AS DOUBLE) + TRY_CAST("KOGAINLOSS" AS DOUBLE)) as ag
				FROM ${table}
				WHERE TRY_CAST("TODOCDUEDATE" AS BIGINT) >= ${monthStarts[0]}
					AND TRY_CAST("TODOCDUEDATE" AS BIGINT) < ${monthStarts[12]}
					${supplierFilter}${agentFilter}
			) sub
			GROUP BY mi
			ORDER BY mi
		`);
		this.checkInterrupted(jobId);

		// Initialize monthly arrays for each bucket
		const onTimeMonthly = new Array(12).fill(0);
		const thirtyDaysMonthly = new Array(12).fill(0);
		const overThirtyMonthly = new Array(12).fill(0);

		for (const row of result) {
			const mi = Number(row.mi);
			if (mi >= 0 && mi < 12) {
				onTimeMonthly[mi] = Number(row.on_time) || 0;
				thirtyDaysMonthly[mi] = Number(row.thirty_days) || 0;
				overThirtyMonthly[mi] = Number(row.over_thirty) || 0;
			}
		}

		this.xAxisData = Array.from({ length: 12 }, (unused, i) =>
			moment().year(year).month(i).format('MMM'),
		);

		this.series = [
			{
				name: 'On Time',
				type: 'bar',
				stack: 'a',
				barWidth: '40%',
				color: GobiColor.COLOR1,
				data: onTimeMonthly,
			},
			{
				name: '30 Days',
				type: 'bar',
				stack: 'a',
				barWidth: '40%',
				color: GobiColor.COLOR2,
				data: thirtyDaysMonthly,
			},
			{
				name: '> 30 Days',
				type: 'bar',
				stack: 'a',
				barWidth: '40%',
				color: GobiColor.COLOR3,
				data: overThirtyMonthly,
			},
		] as any;

		this.generateLoadingText(1);
		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
